/**
 * SVD-native cascade / selection / price engine — the SVD peer of `productHelper.ts`, one module for the
 * whole SVD read surface: cascade + selection, variant resolution (server-resolved / min-price), buy-box
 * pricing derivations (comparison-brand traditional price, promo store-credit), and variant attribute-value
 * reads.
 *
 * For the SVD flow only: it operates purely on the SVD shapes carried directly by the `Product` domain
 * (`product.vdas` for the ordered VDAs, `product.variants` for the raw variants that carry `attributeValues`)
 * plus the SVD-native selection type (`SvdSelection[]`). It **never** constructs or reads a legacy
 * `ProductOption` — the two flows stay fully separate (see `pdp-purchase-widget-solutioning.md`).
 *
 * Implements the LLD §5 algorithms (`pdp-lld.md`):
 * - only **ACTIVE** SKUs drive availability / assumed-variant (§5.1, §2.6);
 * - availability is **unidirectional** — a VDA is narrowed only by selected VDAs earlier in `.order` (§5.1);
 * - selection reconcile on change (§5.2): later VDAs re-evaluate top-down; a still-valid value is kept, a
 *   previously-selected-but-now-invalid one falls back to its first available value, the **primary swatch
 *   always re-selects** (never left unset — the "color reset"), and a previously-unselected VDA stays unset.
 *
 * The SVD product is an `SvdProduct` (subclass of `Product`), its variants are `SvdProductVariant`
 * carrying the SVD fields (`vdas` coord map, `attributeValues`, `price`, `id`, `status`). Callers pass a
 * `Product` (the shared dual-purpose form type); these helpers view it as an `SvdProduct` — the flow is
 * only ever entered on isSvd-gated paths.
 */

import { PRODUCT_VARIANT_STATUS } from '../domains/ProductVariant/BaseProductVariant'
import {
  VdaType,
  type SvdSelection,
  type SvdVariantAttributeValue,
  type SvdVda,
} from '../domains/svd/svd-product.types'
import { getValidPromo } from '../utils/festivalOffersUtility'

import type Product from '../domains/Product'
import type SvdProduct from '../domains/svd/svd-product'
import type SvdProductVariant from '../domains/svd/svd-product-variant'

// SVD-MIGRATION(narrow): the SVD flow's `Product` is always an `SvdProduct` (callers are isSvd-gated), so
// view it as one to reach the SVD-only fields (`vdas` descriptors, `SvdProductVariant` variants). Removed
// once the domain is unified and callers pass `SvdProduct` directly.
const asSvd = (product: Product): SvdProduct => product as unknown as SvdProduct

/**
 * A variant's canonical value for a VDA key — the V2 coord map (`VariantCore.vdas`), a T0 field always
 * present in the layout fetch. Returns `undefined` when the variant doesn't participate in that VDA.
 */
const coordOf = (variant: SvdProductVariant, vdaKey: string): string | undefined => variant.vdas[vdaKey]

/** Only ACTIVE SKUs drive availability / cascade / assumed-variant (LLD §5.1, §2.6). */
const getActiveVariants = (product: Product): SvdProductVariant[] =>
  (asSvd(product).variants ?? []).filter(
    variant => typeof variant === 'object' && variant !== null && variant.status === PRODUCT_VARIANT_STATUS.ACTIVE,
  )

/** VDAs ordered by `.order` (stable for ties — preserves wire order). */
const getOrderedVdas = (product: Product): SvdVda[] =>
  [...(asSvd(product).vdas ?? [])].sort((a, b) => a.order - b.order)

/**
 * Primary swatch VDA key. Prefers the server-authored `isPrimarySwatch` marker (proto
 * `VdaDescriptor.isPrimarySwatch`); falls back to the lowest-`.order` SWATCH-type VDA when no VDA is
 * flagged (pre-V2 inputs / robustness). LLD §5.3.
 */
export const getPrimarySwatchKey = (product: Product): string | undefined => {
  const orderedVdas = getOrderedVdas(product)
  return orderedVdas.find(vda => vda.isPrimarySwatch)?.key ?? orderedVdas.find(vda => vda.type === VdaType.SWATCH)?.key
}

/** True when the variant satisfies every selected `{ vdaKey, valueKey }`. */
const variantMatchesSelection = (variant: SvdProductVariant, selected: SvdSelection[]): boolean =>
  selected.every(({ vdaKey, valueKey }) => coordOf(variant, vdaKey) === valueKey)

/**
 * Core primitive — the ACTIVE variants matching every selected VDA value. With an empty selection every
 * active variant matches. Only the keys present in `selected` take part in filtering (unselected VDAs and
 * non-VDA attributes such as `description` are ignored), mirroring `productHelper.getVariantsForOptions`.
 */
export function getSvdVariantsForOptions(product: Product, selected: SvdSelection[]): SvdProductVariant[] {
  return getActiveVariants(product).filter(variant => variantMatchesSelection(variant, selected))
}

/**
 * Cascade narrowing (unidirectional, LLD §5.1): the value keys of `vda` still reachable given only the
 * selections on VDAs strictly EARLIER in `.order` (lower order number). VDAs that share `vda`'s order —
 * or come later — never constrain it; unselected earlier VDAs pass their full set through. Dedups.
 *
 * Precedence is the `.order` NUMBER, not wire/array position (LLD §5.1). Distinct orders are the BE
 * contract; if orders happen to tie, equal-order VDAs simply don't narrow each other (no cross-cascade)
 * — a graceful degenerate case, never wrong behavior. Kept identical in basis to `getUpdatedSvdSelection`
 * so availability and reconcile always agree.
 */
export function getAvailableValueKeysForVda(product: Product, vda: SvdVda, selected: SvdSelection[]): string[] {
  const orderByVdaKey = new Map(getOrderedVdas(product).map(v => [v.key, v.order]))
  const earlierSelections = selected.filter(s => (orderByVdaKey.get(s.vdaKey) ?? Number.POSITIVE_INFINITY) < vda.order)

  const reachable = new Set(
    getSvdVariantsForOptions(product, earlierSelections)
      .map(variant => coordOf(variant, vda.key))
      .filter((key): key is string => Boolean(key)),
  )
  return vda.values.map(value => value.key).filter(key => reachable.has(key))
}

/** First available value of `vda` in display order (`.order`), given the context selections (LLD §5.1). */
const firstAvailableValueKey = (product: Product, vda: SvdVda, context: SvdSelection[]): string | undefined => {
  const available = new Set(getAvailableValueKeysForVda(product, vda, context))
  return [...vda.values].sort((a, b) => a.order - b.order).find(value => available.has(value.key))?.key
}

/**
 * Apply a selection on `vdaKey` (replace/append), then reconcile every VDA later in `.order` top-down
 * (LLD §5.2). Earlier VDAs and the changed VDA are left untouched (unidirectional). For each later VDA,
 * against the running context: keep a still-valid value; otherwise the primary swatch and any
 * previously-selected VDA fall back to their first available value, a previously-unselected VDA stays
 * unset, and a VDA with no available value (∅) is dropped.
 */
export function getUpdatedSvdSelection(
  product: Product,
  currentSelected: SvdSelection[],
  vdaKey: string,
  valueKey: string,
): SvdSelection[] {
  const orderedVdas = getOrderedVdas(product)
  const changedOrder = orderedVdas.find(v => v.key === vdaKey)?.order ?? Number.POSITIVE_INFINITY
  const primarySwatchKey = getPrimarySwatchKey(product)
  const wasSelected = new Set(currentSelected.map(s => s.vdaKey))

  // Running selection: current selections + the changed VDA (replace/append).
  const next = new Map(currentSelected.map(s => [s.vdaKey, s.valueKey]))
  next.set(vdaKey, valueKey)

  const contextOf = (): SvdSelection[] => [...next].map(([k, v]) => ({ vdaKey: k, valueKey: v }))

  for (const vda of orderedVdas) {
    if (vda.order <= changedOrder) continue // only VDAs later in `.order` cascade (unidirectional)
    const context = contextOf()
    const available = new Set(getAvailableValueKeysForVda(product, vda, context))
    const current = next.get(vda.key)

    if (current !== undefined && available.has(current)) {
      // still valid → keep untouched, no need to compute a fallback
      continue
    }

    const fallback = firstAvailableValueKey(product, vda, context)
    if (available.size === 0 || fallback === undefined) {
      next.delete(vda.key) // no ACTIVE SKU in context → the VDA disappears
    } else if (vda.key === primarySwatchKey || wasSelected.has(vda.key)) {
      // primary swatch always re-selects; a previously-selected VDA falls back to first available
      next.set(vda.key, fallback)
    } else {
      next.delete(vda.key) // was unselected → stay unselected
    }
  }

  const reconciled: SvdSelection[] = []
  orderedVdas.forEach(vda => {
    const valueKey = next.get(vda.key)
    if (valueKey !== undefined) reconciled.push({ vdaKey: vda.key, valueKey })
  })
  return reconciled
}

/**
 * The matching ACTIVE variant with the lowest price. It is the SVD buy box's fallback resolver — used by
 * `resolveSelectedVariant` once the shopper changes the selection (no server variant id) — and by the
 * shared product-form price display (`useProductFormWrapper` / `use-product-form`). The *initial* resolved
 * variant is the server-computed one (`resolveSelectedVariant` with `serverVariantId`); the PL/enrich API
 * owns the first-SKU default (WG MoM 2026-07-28), so the client does not re-derive it.
 */
export function getSvdVariantWithMinPrice(product: Product, selected: SvdSelection[]): SvdProductVariant | undefined {
  const variants = getSvdVariantsForOptions(product, selected)
  if (!variants.length) return undefined
  return variants.reduce((min, variant) => ((variant.price ?? Infinity) < (min.price ?? Infinity) ? variant : min))
}

/**
 * Whether the resolved variant is app-exclusive — the SVD-native analog of legacy
 * `getIsSelectedColorAppExclusive` (which keyed on the selected color). SVD models `appExclusive` at the
 * VARIANT level (a T0 core field), so it reflects the current selection directly. Drives the "Shop in app"
 * CTA + the grey app-exclusive banner. False when no variant resolves.
 */
export function getSvdIsAppExclusive(product: Product, selected: SvdSelection[], serverVariantId?: number): boolean {
  return Boolean(resolveSelectedVariant(product, selected, serverVariantId)?.appExclusive)
}

/**
 * The variant to treat as selected. When a `serverVariantId` is supplied (the widget payload's
 * server-resolved `selection.variantId`) it is authoritative — the matching variant is returned directly,
 * honouring the server's resolution strategy (the API already applies the first-SKU default, WG MoM
 * 2026-07-28). Otherwise the variant is resolved from `selected` as the min-price assumed variant (§5.4).
 * The caller stops passing `serverVariantId` once the shopper changes the selection (at which point the
 * server id is stale and min-price takes over).
 */
export function resolveSelectedVariant(
  product: Product,
  selected: SvdSelection[],
  serverVariantId?: number,
): SvdProductVariant | undefined {
  if (serverVariantId !== undefined) {
    return (asSvd(product).variants ?? []).find(variant => variant.id === serverVariantId)
  }
  return getSvdVariantWithMinPrice(product, selected)
}

/**
 * Placeholder preselection: the first *available* value (by `.order`) of each VDA, cascaded top-down so
 * the result is always backed by an ACTIVE SKU. This is intentionally NOT the full LLD §5.3 primary-swatch
 * / card explicit-vs-assumed logic — that is tracked separately as backlog.
 */
export function getInitialSvdSelection(product: Product): SvdSelection[] {
  const selection: SvdSelection[] = []
  getOrderedVdas(product).forEach(vda => {
    const valueKey = firstAvailableValueKey(product, vda, selection)
    if (valueKey) selection.push({ vdaKey: vda.key, valueKey })
  })
  return selection
}

/**
 * The selection to seed the buy box with on first render. Prefers the **server-computed** default —
 * `serverValues`, the canonical VDA-key → value-key map from the widget payload's `selection.values`
 * (`ProductPurchaseV2WidgetData.selection`), which the PL service already resolved to an ACTIVE
 * `variantId` (LLD §5.4). The map is a widget-data concern the caller passes in — it is deliberately NOT
 * read off the domain. Entries are mapped into `SvdSelection[]` in VDA `.order`; only the keys the server
 * sent are used (unknown keys are ignored; VDAs the server left out stay unselected). Falls back to the
 * client-derived placeholder (`getInitialSvdSelection`) **only** when the server sent no usable selection
 * at all — rare, and a signal the PL service under-populated the payload.
 */
export function resolveInitialSvdSelection(product: Product, serverValues?: Record<string, string>): SvdSelection[] {
  if (serverValues) {
    const selection: SvdSelection[] = []
    getOrderedVdas(product).forEach(vda => {
      const valueKey = serverValues[vda.key]
      if (valueKey) selection.push({ vdaKey: vda.key, valueKey })
    })
    if (selection.length > 0) return selection
  }
  return getInitialSvdSelection(product)
}

/**
 * Traditional (comparison-brand) price for the **resolved variant** — mirrors V1's
 * `productHelper.getBrandComparison` + `getTraditionalPriceForOptions`, keyed off the resolved variant id
 * (not the legacy options model). Pass the variant already resolved by `resolveSelectedVariant` so every
 * published field derives from the one variant. Keep each brand's price entry for that id (`price > 0`),
 * prefer `showOnSite` brands when any exist (else all brands), and take the minimum. `undefined` when no
 * comparison brand prices the variant (e.g. no `comparisonBrands`, or no resolved variant).
 */
export function getSvdTraditionalPrice(product: Product, variant?: SvdProductVariant): number | undefined {
  const variantId = variant?.id
  if (variantId === undefined) return undefined

  const brandPrices = (asSvd(product).comparisonBrands ?? [])
    .map(cb => {
      const brandPrice = cb.prices.find(cp => cp.variantId === variantId)
      return { showOnSite: cb.showOnSite, price: brandPrice && brandPrice.price > 0 ? brandPrice.price : undefined }
    })
    .filter((cb): cb is { showOnSite: boolean; price: number } => cb.price !== undefined)

  const showOnSite = brandPrices.filter(cb => cb.showOnSite)
  const scoped = showOnSite.length > 0 ? showOnSite : brandPrices
  if (scoped.length === 0) return undefined

  const price = Math.min(...scoped.map(cb => cb.price))
  return Number.isFinite(price) ? price : undefined
}

/**
 * Store-credit grant for the **resolved variant** — its currently-valid promo's `storeCreditGrant`, or `0`
 * when there is none (the V2 sample case). Mirrors V1's festival-util `getStoreCreditGrant`. Pass the
 * variant already resolved by `resolveSelectedVariant`.
 */
export function getSvdStoreCreditGrant(variant?: SvdProductVariant): number {
  return getValidPromo(variant?.promoConfig)?.storeCreditGrant ?? 0
}

/**
 * Read a NUMERIC variant `attributeValues` entry's `localisedValue` as a number, at the point of use. The
 * SVD buy-box scalars (servings / per-serving price) live in the variant's `attributeValues` map — a V2
 * field read from there directly rather than adapted onto legacy `BaseProductVariant` scalar fields. Blank
 * / non-numeric → `undefined` (so `""` is "absent", not `0`).
 */
export const numericAttribute = (av?: SvdVariantAttributeValue): number | undefined => {
  const n = parseFloat(av?.localisedValue ?? '')
  return Number.isFinite(n) ? n : undefined
}
